"use client";

import { X } from "lucide-react";
import { useState } from "react";

const ManageColumnsModal = ({ isOpen, onClose, onSave, initialColumns, tableType }) => {
    // Initialize columns from localStorage or initialColumns
    const [columns, setColumns] = useState(() => {
        if (typeof window === "undefined") {
            // Server-side: use initialColumns or default to all true
            const initialState = {};
            const columnKeys = Array.isArray(initialColumns)
                ? initialColumns.map(col => col.id)
                : Object.keys(initialColumns);
            columnKeys.forEach(key => {
                initialState[key] = true;
            });
            return initialState;
        }

        // Client-side: try to load from localStorage
        const savedColumns = localStorage.getItem(`${tableType}TableColumns`);
        if (savedColumns) {
            try {
                return JSON.parse(savedColumns);
            } catch (e) {
                console.error("Error parsing localStorage columns:", e);
            }
        }

        // Fallback: use initialColumns or default to all true
        const initialState = {};
        const columnKeys = Array.isArray(initialColumns)
            ? initialColumns.map(col => col.id)
            : Object.keys(initialColumns);
        columnKeys.forEach(key => {
            initialState[key] = initialColumns[key] !== undefined ? initialColumns[key] : true;
        });
        return initialState;
    });

    if (!isOpen) return null;

    const handleCheckboxChange = (columnId) => {
        const updatedColumns = {
            ...columns,
            [columnId]: !columns[columnId],
        };
        setColumns(updatedColumns);
        localStorage.setItem(`${tableType}TableColumns`, JSON.stringify(updatedColumns));
        onSave(updatedColumns);
    };

    // Determine if initialColumns is an array (with id/label) or object (key-value)
    const isArrayColumns = Array.isArray(initialColumns);
    const columnEntries = isArrayColumns
        ? initialColumns.map(col => ({ id: col.id, label: col.label }))
        : Object.keys(initialColumns).map(key => ({ id: key, label: key }));

    return (
        <div className="min-h-screen fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg m-4 p-6 w-[665px] relative">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute shadow-md -top-4 -right-4 bg-white p-2 text-[#989191] rounded-full"
                >
                    <X className="w-6 h-6" />
                </button>

                {/* Modal Header */}
                <h2 className="text-lg font-semibold text-gray-800 mb-2">
                    Manage Columns
                </h2>
                <p className="text-sm text-black mb-4">
                    Checked [✓] columns will display in your table.
                </p>

                {/* Modal Content */}
                <div className="space-y-6">
                    <div className="p-4 bg-[#284F9E0D] rounded">
                        <p className="text-sm font-semibold text-[#284F9E] mb-4">
                            {tableType} Columns
                        </p>
                        <div className="space-y-3">
                            {columnEntries.map(({ id, label }) => (
                                <label
                                    key={id}
                                    className="flex items-center justify-between space-x-2"
                                >
                                    <span className="text-gray-700 text-sm">{label}</span>
                                    <input
                                        type="checkbox"
                                        className="form-checkbox w-5 h-5 text-black bg-[#284F9E]"
                                        checked={columns[id]}
                                        onChange={() => handleCheckboxChange(id)}
                                    />
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManageColumnsModal;